import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import CustomerOnlinePaymentStatus from "../../components/CustomerOnlinePaymentStatus";
import OrdersSectionMobile from "./OrdersSectionMobile";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import { FiShoppingBag } from "react-icons/fi";
import {
  getPaymentStatusLabel,
  getPaymentStatusTone,
} from "../../utils/orderPaymentStatus";
import { buildProductDetailPath } from "../../utils/productLinks";
import { getProductDisplayName } from "../../utils/productDisplayName";
import {
  getDeliveryMethodLabel,
  isBranchDelivery,
  isKyivCourierDelivery,
  isNovaPoshtaCourierDelivery,
} from "../../api/checkoutDeliverySettingsApi";
import { isOrderFreeDelivery } from "../../utils/freeDelivery";
import styles from "./OrdersSection.module.css";

// === Мапа доставки (узгоджено з мобільною) ===
const DELIVERY_METHODS_MAP = {
  branch: "У відділення Нової пошти",
  locker: "У поштомат Нової пошти",
  courier: "Курʼєром",
  "courier-nova-poshta": "Курʼєром Нової пошти",
  "courier-kyiv": "Курʼєром по Києву",
  pickup: "Самовивіз",
};

// === Текст вартості доставки (ідентично) ===
function getDeliveryCostText(order) {
  if (isOrderFreeDelivery(order)) return "безкоштовно";

  return "за тарифами перевізника";
}

// === Парсер для відділення НП (як у мобільному) ===
const parseBranch = (branchString) => {
  if (!branchString) return { number: "", weightLimit: "", address: "" };
  const matchNumber = branchString.match(/№\d+/);
  const number = matchNumber ? matchNumber[0] : "";
  const matchWeight = branchString.match(/\(([^)]+)\)/);
  const weightLimit = matchWeight ? matchWeight[1] : "";
  const address = branchString.split(":")[1]?.trim() || "";
  return { number, weightLimit, address };
};

const formatMoney = (value) =>
  `${new Intl.NumberFormat("uk-UA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0)} ₴`;

const getProductImageSrc = (item = {}) => {
  const imageCandidate =
    item.image ||
    item.imageUrl ||
    item.productImage ||
    item.mainImage ||
    item.product?.image ||
    item.product?.imageUrl ||
    item.images?.[0] ||
    item.product?.images?.[0];

  if (typeof imageCandidate === "string" && imageCandidate.trim()) {
    return imageCandidate;
  }

  if (imageCandidate?.url) return imageCandidate.url;
  if (imageCandidate?.src) return imageCandidate.src;

  return "/no-image.png";
};

const getPaymentDetails = (order = {}) => order.paymentDetails || {};

const getPaymentPurpose = (order = {}) => {
  const details = getPaymentDetails(order);
  return (
    details.paymentPurpose ||
    `${details.purposePrefix || "Оплата замовлення"} №${order.orderNumber || "—"}`
  );
};

// === Кольори статусів замовлення (для відображення під датою) ===
const STATUS_STYLE_MAP = {
  Новий: { color: "#1976d2", background: "#e3f2fd" },
  "В обробці": { color: "#1976d2", background: "#e3f2fd" },
  Відправлено: { color: "#ed6c02", background: "#fff3e0" },
  Отримано: { color: "#2e7d32", background: "#e8f5e9" },
  Скасовано: { color: "#b71c1c", background: "#fdecea" },
  "Відмова від отримання": { color: "#b71c1c", background: "#fdecea" },
  Повернено: { color: "#b71c1c", background: "#fdecea" },
  "Частково повернено": { color: "#b71c1c", background: "#fdecea" },
  "Очікує доплати": { color: "#b26a00", background: "#fff3e0" },
};

const OrdersSection = () => {
  const [orders, setOrders] = useState([]);
  const [openDetails, setOpenDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);

  // === Пагінація ===
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 6;

  const getPageNumbers = () => {
    const totalPages = Math.ceil(orders.length / ordersPerPage);
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "dots", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1, "dots");
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(
          1,
          "dots",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "dots",
          totalPages
        );
      }
    }
    return pages;
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await axiosInstance.get("/customers/me/orders");
        setOrders(res.data);
      } catch {
        setError("Помилка при отриманні замовлень");
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  // Якщо кількість сторінок зменшилась (напр., змінились дані) — підрізаємо currentPage
  useEffect(() => {
    const max = Math.max(1, Math.ceil(orders.length / ordersPerPage));
    if (currentPage > max) setCurrentPage(max);
  }, [orders.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div>Завантаження замовлень…</div>;
  if (error) return <div style={{ color: "red" }}>{error}</div>;
  if (!orders.length) return <div>У вас ще немає замовлень.</div>;

  const sectionHeader = (
    <div className={styles.sectionHeader}>
      <div className={styles.sectionIcon}>
        <FiShoppingBag />
      </div>
      <div>
        <span>Покупки</span>
        <h1>Мої замовлення</h1>
        <p>Переглядайте історію покупок, оплату, доставку та склад замовлення.</p>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div className={styles.ordersContainer}>
        {sectionHeader}
        <OrdersSectionMobile orders={orders} />
      </div>
    );
  }

  const getProductHref = (it) => {
    const s = it.slug || it.productSlug || it.product?.slug;
    if (s) {
      return buildProductDetailPath({
        ...it,
        slug: s,
        variantId: it.variantId || it.productId || it._id,
      });
    }
    return null;
  };

  // === Розрахунок поточної сторінки ===
  const totalPages = Math.ceil(orders.length / ordersPerPage);
  const indexOfLast = currentPage * ordersPerPage;
  const indexOfFirst = indexOfLast - ordersPerPage;
  const currentOrders = orders.slice(indexOfFirst, indexOfLast);

  return (
    <div className={styles.ordersContainer}>
      {sectionHeader}
      {currentOrders.map((order) => {
        const statusStyle = STATUS_STYLE_MAP[order.status] || {
          color: "#555",
          background: "#f0f0f0",
        };
        const paymentStatusTone = getPaymentStatusTone(order);
        const cartItems = Array.isArray(order.cartItems)
          ? order.cartItems
          : [];
        const { number, weightLimit, address } = parseBranch(
          order.branchNumber
        );
        const paymentDetails = getPaymentDetails(order);
        const deliveryLabel =
          DELIVERY_METHODS_MAP[order.deliveryMethod] ||
          getDeliveryMethodLabel(order.deliveryMethod) ||
          order.deliveryMethod;
        const paymentStatusLabel = getPaymentStatusLabel(order);

        return (
          <div key={order._id} className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderLeft}>
                <div className={styles.orderMetaBlock}>
                  <div>
                    <div className={styles.orderNumber}>
                      <span className={styles.orderNumberLabel}>
                        Замовлення
                      </span>
                      <span>№ {order.orderNumber}</span>
                    </div>
                    <div className={styles.date}>
                      {new Date(order.createdAt).toLocaleString("uk-UA")}
                    </div>
                    <div
                      className={styles.statusBadge}
                      style={{
                        "--status-color": statusStyle.color,
                        "--status-bg": statusStyle.background,
                      }}
                    >
                      {order.status}
                    </div>
                  </div>
                </div>

                <div className={styles.previewImagesWrapper}>
                  {cartItems.map((item, i) => (
                    <img
                      key={item.productId || i}
                      src={getProductImageSrc(item)}
                      alt={item.name}
                      className={styles.previewImage}
                      loading="lazy"
                      decoding="async"
                    />
                  ))}
                </div>
              </div>

              <div className={styles.headerActions}>
                <button
                  onClick={() =>
                    setOpenDetails(openDetails === order._id ? null : order._id)
                  }
                  className={styles.toggleBtn}
                  type="button"
                  aria-expanded={openDetails === order._id}
                  aria-label={
                    openDetails === order._id
                      ? "Згорнути деталі замовлення"
                      : "Розкрити деталі замовлення"
                  }
                >
                  {openDetails === order._id ? <IoChevronUp /> : <IoChevronDown />}
                </button>
              </div>
            </div>

            {/* Деталі замовлення */}
            {openDetails === order._id && (
              <div className={styles.cardDetails}>
                <div className={styles.detailsGrid}>
                  <section className={styles.detailsSection}>
                    <h3 className={styles.cardDetailsTitle}>
                      Загальна інформація
                    </h3>
                    <div className={styles.infoList}>
                      <div className={styles.infoItem}>
                        <span>Клієнт</span>
                        <b>{order.customerName}</b>
                      </div>
                      <div className={styles.infoItem}>
                        <span>Телефон</span>
                        <b>{order.phone}</b>
                      </div>
                      <div className={styles.infoItem}>
                        <span>Email</span>
                        <b>{order.email || "—"}</b>
                      </div>
                      <div className={styles.infoItem}>
                        <span>Сума замовлення</span>
                        <b>{formatMoney(order.totalAmount)}</b>
                      </div>
                      <div className={styles.infoItem}>
                        <span>Спосіб оплати</span>
                        <b>{order.paymentMethod}</b>
                      </div>
                      <div className={styles.infoItem}>
                        <span>Статус оплати</span>
                        <b className={styles[paymentStatusTone] || styles.paymentUnknown}>
                          {paymentStatusLabel}
                        </b>
                      </div>
                    </div>
                  </section>

                  <section className={styles.detailsSection}>
                    <h3 className={styles.cardDetailsTitle}>Доставка</h3>
                    <div className={styles.infoList}>
                      <div className={styles.infoItem}>
                        <span>Спосіб доставки</span>
                        <b>{deliveryLabel}</b>
                      </div>

                      {isBranchDelivery(order.deliveryMethod) && (
                        <>
                          <div className={styles.infoItem}>
                            <span>Місто</span>
                            <b>{order.city || "—"}</b>
                          </div>
                          <div className={styles.infoItem}>
                            <span>Відділення НП</span>
                            <b>
                              {number || "—"}
                              {weightLimit ? ` (${weightLimit})` : ""}
                            </b>
                          </div>
                          {address && (
                            <div className={styles.infoItem}>
                              <span>Адреса відділення</span>
                              <b>{address}</b>
                            </div>
                          )}
                        </>
                      )}

                      {isNovaPoshtaCourierDelivery(order.deliveryMethod) && (
                        <>
                          <div className={styles.infoItem}>
                            <span>Місто</span>
                            <b>{order.city || "—"}</b>
                          </div>
                          <div className={styles.infoItem}>
                            <span>Адреса</span>
                            <b>
                              вул. {order.street || "—"}, буд.{" "}
                              {order.buildingNumber || "—"}
                              {order.entranceNumber
                                ? `, під'їзд ${order.entranceNumber}`
                                : ""}
                            </b>
                          </div>
                          {order.courierNote && (
                            <div className={styles.infoItem}>
                              <span>Примітка для кур’єра</span>
                              <b>{order.courierNote}</b>
                            </div>
                          )}
                        </>
                      )}

                      {isKyivCourierDelivery(order.deliveryMethod) && (
                        <>
                          <div className={styles.infoItem}>
                            <span>Адреса</span>
                            <b>
                              вул. {order.street || "—"}, буд.{" "}
                              {order.buildingNumber || "—"}
                              {order.entranceNumber
                                ? `, під'їзд ${order.entranceNumber}`
                                : ""}
                            </b>
                          </div>
                          {order.courierNote && (
                            <div className={styles.infoItem}>
                              <span>Примітка для кур’єра</span>
                              <b>{order.courierNote}</b>
                            </div>
                          )}
                          {order.deliveryService && (
                            <div className={styles.infoItem}>
                              <span>Сервіс доставки</span>
                              <b>{order.deliveryService}</b>
                            </div>
                          )}
                          {order.car && (
                            <div className={styles.infoItem}>
                              <span>Автомобіль</span>
                              <b>{order.car}</b>
                            </div>
                          )}
                          {order.driver && (
                            <div className={styles.infoItem}>
                              <span>Водій</span>
                              <b>{order.driver}</b>
                            </div>
                          )}
                          {order.driverPhone && (
                            <div className={styles.infoItem}>
                              <span>Телефон водія</span>
                              <b>{order.driverPhone}</b>
                            </div>
                          )}
                        </>
                      )}

                      {order.deliveryMethod === "pickup" && (
                        <div className={styles.infoItem}>
                          <span>Адреса самовивозу</span>
                          <b>м. Київ, вул. ... (адреса самовивозу)</b>
                        </div>
                      )}

                      {order.ttn && (
                        <div className={styles.infoItem}>
                          <span>ТТН</span>
                          <b>{order.ttn}</b>
                        </div>
                      )}

                      <div className={styles.infoItem}>
                        <span>Вартість доставки</span>
                        <b>{getDeliveryCostText(order)}</b>
                      </div>
                    </div>
                  </section>
                </div>

                <CustomerOnlinePaymentStatus order={order} />

                {order.paymentMethod === "Оплата за реквізитами" && (
                  <section className={styles.detailsSection}>
                    <h3 className={styles.cardDetailsTitle}>
                      Реквізити для оплати
                    </h3>

                    <div className={styles.requisitesGrid}>
                      <div className={styles.infoItem}>
                        <span>Отримувач</span>
                        <b>{paymentDetails.recipientName || "—"}</b>
                      </div>
                      <div className={styles.infoItem}>
                        <span>IBAN</span>
                        <b>{paymentDetails.iban || "—"}</b>
                      </div>
                      <div className={styles.infoItem}>
                        <span>РНОКПП / ЄДРПОУ</span>
                        <b>{paymentDetails.edrpou || "—"}</b>
                      </div>
                      <div className={styles.infoItem}>
                        <span>Банк</span>
                        <b>{paymentDetails.bankName || "—"}</b>
                      </div>
                      <div className={styles.infoItem}>
                        <span>Сума до оплати</span>
                        <b>
                          {formatMoney(paymentDetails.amount || order.totalAmount || 0)}
                        </b>
                      </div>
                      <div className={styles.infoItem}>
                        <span>Призначення платежу</span>
                        <b>{getPaymentPurpose(order)}</b>
                      </div>
                    </div>

                    <div className={styles.requisitesNotice}>
                      Відправлення замовлення здійснюється після надходження{" "}
                      <strong>100% оплати</strong> на рахунок продавця.
                    </div>
                  </section>
                )}

                <section className={styles.detailsSection}>
                  <h3 className={styles.cardDetailsTitle}>
                    Придбані товари
                  </h3>
                  <div className={styles.itemsTable}>
                    <div className={styles.itemsTableHeader}>
                      <span>Артикул</span>
                      <span>Товар</span>
                      <span>Ціна</span>
                      <span>Кількість</span>
                      <span>Сума</span>
                    </div>
                    {cartItems.map((item, idx) => {
                      const href = getProductHref(item);
                      const itemPrice = Number(item.price || 0);
                      const itemQuantity = Number(item.quantity || 0);

                      return (
                        <div
                          key={item.productId || idx}
                          className={styles.productRow}
                        >
                          <span className={styles.productCode}>
                            {item.productCode || "—"}
                          </span>
                          <div className={styles.productMain}>
                            <img
                              src={getProductImageSrc(item)}
                              alt={item.name}
                              className={styles.itemImage}
                              loading="lazy"
                              decoding="async"
                            />
                            <div className={styles.itemInfo}>
                              {href ? (
                                <Link
                                  to={href}
                                  className={styles.productNameLink}
                                >
                                  {getProductDisplayName(item)}
                                </Link>
                              ) : (
                                <span className={styles.productNameText}>
                                  {getProductDisplayName(item)}
                                </span>
                              )}
                              <span className={styles.productMeta}>
                                {item.manufacturer || "Виробник не вказаний"}
                              </span>
                            </div>
                          </div>
                          <span className={styles.productValue}>
                            {formatMoney(itemPrice)}
                          </span>
                          <span className={styles.productValue}>
                            {itemQuantity}
                          </span>
                          <b className={styles.prodTotal}>
                            {formatMoney(itemQuantity * itemPrice)}
                          </b>
                        </div>
                      );
                    })}
                  </div>
                </section>

                {order.comment && (
                  <div className={styles.comment}>
                    <b>Коментар:</b> {order.comment}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* === Пагінація === */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageButton}
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            🡨
          </button>
          {getPageNumbers().map((n, i) =>
            n === "dots" ? (
              <span key={`dots-${i}`} className={styles.pageButton}>
                …
              </span>
            ) : (
              <button
                key={n}
                className={`${styles.pageButton} ${
                  currentPage === n ? styles.activePage : ""
                }`}
                onClick={() => setCurrentPage(n)}
              >
                {n}
              </button>
            )
          )}
          <button
            className={styles.pageButton}
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            🡪
          </button>
        </div>
      )}
    </div>
  );
};

export default OrdersSection;
